import { CheckCircle, Warning } from '@mui/icons-material';
import remarkGfm from 'remark-gfm';
import ReactMarkdown from 'react-markdown';
import { gql, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Box, Typography } from '../../../../1-primative';
import {
  Button,
  CircularProgress,
  StatusTag, Table, TableBody, TableCell, TableHeadCell, TableRow,
} from '../../../../2-component';
import { translateBackend } from '../../../../../assets/i18n/config';
import { ViewFormDialog } from './viewFormDialog';

export const FETCH_FORM_COMPLETIONS = gql`
  query fetchFormCompletions($input: FetchFormCompletionsInput!) {
    fetchFormCompletions(input: $input) {
      formCompletions {
        id
        translatedName {
          en
          fr
        }
        state
        signingType
        integrationFormPack {
          id
          state
        }
      }
      totalCount
    }
  }
`;

export const ManageFormCompletions = ({
  options, onNext, workflowCompletion,
}: {
  options: any,
  onNext: () => void;
  workflowCompletion: any;
}) => {
  const { t } = useTranslation(['workflowCompletions', 'client']);
  const [activeFormCompletion, setActiveFormCompletion] = useState<any>(null);

  // Fetch form completions for this workflow
  const { data, loading } = useQuery(FETCH_FORM_COMPLETIONS, {
    variables: {
      input: {
        filter: {
          workflowCompletionId: workflowCompletion.id,
          states: ['UNSIGNED', 'SIGNED'],
        },
        pagination: {
          perPage: 100, // Fetch all forms for this workflow
        },
      },
    },
    skip: !workflowCompletion?.id,
  });

  const formCompletions = data?.fetchFormCompletions?.formCompletions || [];

  // Separate regular forms from integration forms
  const forms = formCompletions ?? [];

  if (loading) {
    return (
      <Box sx={{
        height: '60vh', display: 'flex', justifyContent: 'center', alignItems: 'center',
      }}>
        <CircularProgress/>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '60vh', display: 'flex', flexDirection: 'column' }}>
      {options?.title && (
        <Typography variant='displayLarge' sx={{ mt: 1 }}>
          {translateBackend(options?.title)}
        </Typography>
      )}
      {options?.subtitle && (
        <Typography display='inline-flex' alignItems='end' variant='bodyLarge' component='div' sx={{ mb: 3, table: { width: '100%' } }}>
          <Box display='inline-block'>
            <ReactMarkdown linkTarget="_blank" remarkPlugins={[remarkGfm]}>{translateBackend(options?.subtitle)}</ReactMarkdown>
          </Box>
        </Typography>
      )}
      <Table sx={{ mb: 3 }}>
        <TableBody data-testid='documents-table'>
          <TableRow>
            <TableHeadCell sx={{ width: '100%' }}>{t('client:document')}</TableHeadCell>
            <TableHeadCell sx={{ minWidth: '140px' }}>{t('client:status')}</TableHeadCell>
          </TableRow>
          {forms.filter((formCompletion: any) => formCompletion.state !== 'ARCHIVED').map((formCompletion: any, index: number) => (
            <TableRow
              data-testid={`document-${index}`}
              key={index}
              hover
              pointer
              onClick={() => setActiveFormCompletion(formCompletion)}
            >
              <TableCell>{translateBackend(formCompletion.translatedName)}</TableCell>
              <TableCell>
                <StatusTag
                  color={formCompletion.state === 'SIGNED' ? 'positive' : 'warning'}
                  icon={formCompletion.state === 'SIGNED' ? CheckCircle : Warning}
                  label={formCompletion.state === 'SIGNED' ? t('client:signed') : t('client:notSigned')}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {activeFormCompletion && (
        <ViewFormDialog
          formCompletionId={activeFormCompletion.id}
          onClose={() => setActiveFormCompletion(null)}
          canArchive={options.allowRemovingFormCompletions}
        />
      )}
      <Box display='flex' justifyContent='end'>
        <Button
          dataTestId='continue-button'
          label={t('continue')}
          sx={{ mt: 3, textAlign: 'center' }}
          onClick={onNext}
        />
      </Box>
    </Box>
  );
};
